import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { RpaService } from './rpa.service';

export type RpaProcessamentoStatus = 'PROCESSANDO' | 'PRONTO' | 'BLOQUEADO' | 'CANCELADO';
export type RpaProcessamentoEmpresaStatus = 'PROCESSANDO' | 'PRONTO' | 'BLOQUEADO';

export type RpaProcessamentoTipo = 'pendencia-inscrita' | 'pendencia-nao-inscrita';

export interface RpaProcessamentoEmpresa {
  codigo: string;
  status: RpaProcessamentoEmpresaStatus;
  mensagem: string;
  atualizadoEm: string;
  /** Caminho do arquivo no bucket para visualização. */
  fileUrl?: string;
}

export interface RpaProcessamento {
  id: string;
  tipo: RpaProcessamentoTipo;
  titulo: string;
  status: RpaProcessamentoStatus;
  mensagem: string;
  inicio: string;
  fim?: string;
  tipoDivida: 'inscrita' | 'nao-inscrita';
  codigosRastreio: string[];
  /** BotId retornado pelo Data Harvester no enqueue. */
  botId?: string;
  /** IDs da execution_requests_outbox retornados no enqueue. */
  outboxRequestIds?: string[];
  totalEmpresas: number;
  empresasConcluidas: number;
  empresas: RpaProcessamentoEmpresa[];
}

const STORAGE_KEY = 'rpa-processamentos-ativos';

@Injectable({ providedIn: 'root' })
export class RpaProcessamentoService {
  private readonly processamentosSubject = new BehaviorSubject<RpaProcessamento[]>(this.loadFromStorage());
  readonly processamentos$ = this.processamentosSubject.asObservable();

  constructor(private readonly rpaService: RpaService) {}

  getProcessamentosEmAndamento(): RpaProcessamento[] {
    return this.processamentosSubject.value.filter((p) => p.status === 'PROCESSANDO');
  }

  /** Processamentos em andamento + pendências recentes ainda sem arquivo na lista da API. */
  getProcessamentosParaTabela(arquivosApi: any[] = []): RpaProcessamento[] {
    const limiteMs = 48 * 60 * 60 * 1000;
    const agora = Date.now();

    return this.processamentosSubject.value.filter((proc) => {
      if (proc.status === 'PROCESSANDO' || proc.status === 'CANCELADO') return true;

      const inicioMs = new Date(proc.inicio).getTime();
      if (Number.isNaN(inicioMs) || agora - inicioMs > limiteMs) return false;
      if (!proc.tipo.startsWith('pendencia')) return false;

      return !this.processamentoJaTemArquivoNaLista(proc, arquivosApi);
    });
  }

  chaveExibicaoPendencia(tipoDivida: 'inscrita' | 'nao-inscrita'): string {
    return tipoDivida === 'nao-inscrita'
      ? 'CONSULTA-PENDENCIAS-NAO-INSCRITAS'
      : 'CONSULTA-PENDENCIAS-INSCRITAS';
  }

  getById(id: string): RpaProcessamento | undefined {
    return this.processamentosSubject.value.find((p) => p.id === id);
  }

  registrarPendencia(
    tipoDivida: 'inscrita' | 'nao-inscrita',
    response: any,
    codigoDigitado: string
  ): RpaProcessamento {
    const codigos = this.extrairCodigos(response, codigoDigitado);
    const naoEncontrados = this.normalizeArray(
      response?.cnpjsInvalidos ?? response?.codigosNaoEncontrados
    );
    const agora = new Date().toISOString();

    const empresas: RpaProcessamentoEmpresa[] = [
      ...codigos.map((codigo) => ({
        codigo,
        status: 'PROCESSANDO' as RpaProcessamentoEmpresaStatus,
        mensagem: 'Aguardando processamento da consulta.',
        atualizadoEm: agora
      })),
      ...naoEncontrados.map((codigo) => ({
        codigo,
        status: 'BLOQUEADO' as RpaProcessamentoEmpresaStatus,
        mensagem: 'CNPJ inválido ou não processado.',
        atualizadoEm: agora
      }))
    ];

    const processamento: RpaProcessamento = {
      id: this.novoId(),
      tipo: tipoDivida === 'nao-inscrita' ? 'pendencia-nao-inscrita' : 'pendencia-inscrita',
      titulo: tipoDivida === 'nao-inscrita' ? 'Pendência estadual não inscrita' : 'Pendência estadual inscrita',
      status: 'PROCESSANDO',
      mensagem: 'Consulta em andamento. Clique em Atualizar status para acompanhar.',
      inicio: agora,
      tipoDivida,
      codigosRastreio: codigos,
      botId: String(response?.botId ?? response?.BotId ?? '').trim() || undefined,
      outboxRequestIds: this.normalizeArray(
        response?.outboxRequestIds ?? response?.OutboxRequestIds
      ),
      totalEmpresas: codigos.length,
      empresasConcluidas: 0,
      empresas
    };

    this.upsert(processamento);
    return processamento;
  }

  /** Atualiza status dos processamentos com base nos arquivos da API (chamado manualmente na tela RPA). */
  atualizarComArquivos(response: any): void {
    const files = response?.items || response?.data || (Array.isArray(response) ? response : []);
    this.aplicarAtualizacaoNosProcessamentos(files);
  }

  /**
   * Atualiza o processamento com o status agregado do bot:
   * pending → pendente; processing (claimed/running) → processando;
   * failed → falha; succeeded (nenhum pending/claimed/failed) → sucesso.
   */
  atualizarComOutboxStatus(processamentoId: string, response: any): RpaProcessamento | null {
    const proc = this.getById(processamentoId);
    if (!proc) return null;

    const agora = new Date().toISOString();
    const overall = String(response?.overallStatus ?? response?.OverallStatus ?? '')
      .trim()
      .toLowerCase();

    const pending = Number(response?.pending ?? response?.Pending ?? 0);
    const claimed = Number(response?.claimed ?? response?.Claimed ?? 0);
    const running = Number(response?.running ?? response?.Running ?? 0);
    const failed = Number(response?.failed ?? response?.Failed ?? 0);

    let empresaStatus: RpaProcessamentoEmpresaStatus = 'PROCESSANDO';
    let empresaMensagem = 'Consulta na fila, aguardando início.';
    let status: RpaProcessamentoStatus = 'PROCESSANDO';
    let mensagem = 'Consulta pendente na fila.';
    let fim: string | undefined;

    if (overall === 'pending') {
      empresaStatus = 'PROCESSANDO';
      empresaMensagem = 'Consulta pendente na fila.';
      status = 'PROCESSANDO';
      mensagem = `Consulta pendente (${pending} lote(s) na fila).`;
    } else if (overall === 'processing') {
      empresaStatus = 'PROCESSANDO';
      empresaMensagem = 'Consulta em processamento.';
      status = 'PROCESSANDO';
      mensagem = `Consulta em processamento (${claimed + running} lote(s)).`;
    } else if (overall === 'failed') {
      empresaStatus = 'BLOQUEADO';
      empresaMensagem = 'Falha no processamento da consulta.';
      status = 'BLOQUEADO';
      mensagem = `Consulta finalizada com falha (${failed} lote(s)).`;
      fim = agora;
    } else {
      // succeeded (ou ausência de pending/claimed/failed)
      empresaStatus = 'PRONTO';
      empresaMensagem = 'Consulta concluída com sucesso.';
      status = 'PRONTO';
      mensagem = `Consulta concluída com sucesso (${proc.empresas.length} CNPJ(s)).`;
      fim = agora;
    }

    const empresas = proc.empresas.map((emp) => {
      if (emp.status === 'BLOQUEADO' && emp.mensagem.includes('inválido')) {
        return emp;
      }

      return {
        ...emp,
        status: empresaStatus,
        mensagem: empresaMensagem,
        atualizadoEm: agora
      };
    });

    const atualizado: RpaProcessamento = {
      ...proc,
      status,
      mensagem,
      fim,
      empresas,
      empresasConcluidas: empresas.filter((e) => e.status === 'PRONTO').length,
      botId: String(response?.botId ?? response?.BotId ?? proc.botId ?? '').trim() || proc.botId
    };

    this.upsert(atualizado);
    return atualizado;
  }

  cancelarProcessamento(id: string): boolean {
    const proc = this.getById(id);
    if (!proc || proc.status !== 'PROCESSANDO') return false;

    const agora = new Date().toISOString();
    const lista = this.processamentosSubject.value.map((p) => {
      if (p.id !== id) return p;

      return {
        ...p,
        status: 'CANCELADO' as RpaProcessamentoStatus,
        mensagem: 'Processamento cancelado pelo usuário.',
        fim: agora,
        empresas: p.empresas.map((emp) =>
          emp.status === 'PROCESSANDO'
            ? {
                ...emp,
                status: 'BLOQUEADO' as RpaProcessamentoEmpresaStatus,
                mensagem: 'Cancelado pelo usuário.',
                atualizadoEm: agora
              }
            : emp
        )
      };
    });

    this.persist(lista);
    return true;
  }

  reiniciarProcessamento(id: string, response: unknown): RpaProcessamento | null {
    const proc = this.getById(id);
    if (!proc || !proc.tipo.startsWith('pendencia')) return null;

    const codigos = proc.codigosRastreio.filter((codigo) => !!String(codigo || '').trim());
    if (codigos.length === 0) return null;

    const agora = new Date().toISOString();
    const naoEncontrados = this.normalizeArray(
      (response as { cnpjsInvalidos?: unknown; codigosNaoEncontrados?: unknown })?.cnpjsInvalidos
        ?? (response as { codigosNaoEncontrados?: unknown })?.codigosNaoEncontrados
    );

    const empresas: RpaProcessamentoEmpresa[] = [
      ...codigos.map((codigo) => ({
        codigo,
        status: 'PROCESSANDO' as RpaProcessamentoEmpresaStatus,
        mensagem: 'Aguardando reprocessamento da consulta.',
        atualizadoEm: agora
      })),
      ...naoEncontrados.map((codigo) => ({
        codigo,
        status: 'BLOQUEADO' as RpaProcessamentoEmpresaStatus,
        mensagem: 'CNPJ inválido ou não processado no reprocessamento.',
        atualizadoEm: agora
      }))
    ];

    const atualizado: RpaProcessamento = {
      ...proc,
      status: 'PROCESSANDO',
      mensagem: 'Reprocessamento em andamento. Clique em Atualizar status para acompanhar.',
      inicio: agora,
      fim: undefined,
      codigosRastreio: codigos,
      botId: String(
        (response as { botId?: string; BotId?: string })?.botId
          ?? (response as { BotId?: string })?.BotId
          ?? proc.botId
          ?? ''
      ).trim() || proc.botId,
      outboxRequestIds: (() => {
        const novos = this.normalizeArray(
          (response as { outboxRequestIds?: unknown; OutboxRequestIds?: unknown })?.outboxRequestIds
            ?? (response as { OutboxRequestIds?: unknown })?.OutboxRequestIds
        );
        return novos.length > 0 ? novos : proc.outboxRequestIds;
      })(),
      totalEmpresas: codigos.length,
      empresasConcluidas: 0,
      empresas
    };

    this.upsert(atualizado);
    return atualizado;
  }

  marcarErro(id: string, mensagem: string): void {
    const lista = this.processamentosSubject.value.map((p) =>
      p.id === id
        ? {
            ...p,
            status: 'BLOQUEADO' as RpaProcessamentoStatus,
            mensagem,
            fim: new Date().toISOString()
          }
        : p
    );
    this.persist(lista);
  }

  private aplicarAtualizacaoNosProcessamentos(files: any[]): void {
    let alterou = false;
    const lista = this.processamentosSubject.value.map((proc) => {
      if (!this.deveSincronizarComArquivos(proc)) return proc;

      const inicio = new Date(proc.inicio);
      const empresas = proc.empresas.map((emp) => {
        if (emp.status !== 'PROCESSANDO' && !(emp.status === 'PRONTO' && !emp.fileUrl)) return emp;

        const match = files.find((f: any) => this.matchArquivo(f, emp.codigo, proc, inicio));
        if (!match) return emp;

        const statusArquivo = String(match?.status || '').toUpperCase();
        const objectName = this.rpaService.resolverObjectNameArquivo(match);
        if (statusArquivo === 'SUCCESS') {
          alterou = true;
          return {
            ...emp,
            status: 'PRONTO' as RpaProcessamentoEmpresaStatus,
            mensagem: 'Arquivo disponível para visualização.',
            fileUrl: objectName || emp.fileUrl,
            atualizadoEm: new Date().toISOString()
          };
        }
        if (['BLOQUED', 'UNEXPECTED', 'INVALID_CNPJ'].includes(statusArquivo)) {
          alterou = true;
          return {
            ...emp,
            status: 'BLOQUEADO' as RpaProcessamentoEmpresaStatus,
            mensagem: `Processo retornou status ${statusArquivo}.`,
            atualizadoEm: new Date().toISOString()
          };
        }
        return emp;
      });

      const concluidas = empresas.filter((e) => e.status === 'PRONTO').length;
      const bloqueadas = empresas.filter((e) => e.status === 'BLOQUEADO').length;
      const terminal = concluidas + bloqueadas;
      const total = proc.totalEmpresas;

      let novo: RpaProcessamento = {
        ...proc,
        empresas,
        empresasConcluidas: concluidas
      };

      if (terminal >= total && total > 0) {
        alterou = true;
        const comBloqueio = bloqueadas > 0;
        novo = {
          ...novo,
          status: (comBloqueio ? 'BLOQUEADO' : 'PRONTO') as RpaProcessamentoStatus,
          mensagem: comBloqueio
            ? 'Processamento concluído com bloqueios.'
            : 'Processamento concluído.',
          fim: new Date().toISOString()
        };
      } else if (terminal < total) {
        alterou = true;
        novo = {
          ...novo,
          mensagem: `Processando ${total - terminal} de ${total} empresa(s)...`
        };
      }

      return novo;
    });

    if (alterou) this.persist(lista);
  }

  private deveSincronizarComArquivos(proc: RpaProcessamento): boolean {
    if (proc.status === 'PROCESSANDO') return true;
    if (!proc.tipo.startsWith('pendencia')) return false;
    const limiteMs = 48 * 60 * 60 * 1000;
    const inicioMs = new Date(proc.inicio).getTime();
    if (Number.isNaN(inicioMs) || Date.now() - inicioMs > limiteMs) return false;
    return proc.empresas.some((e) => e.status === 'PRONTO' && !e.fileUrl);
  }

  private processamentoJaTemArquivoNaLista(proc: RpaProcessamento, arquivos: any[]): boolean {
    const inicio = new Date(proc.inicio);
    return arquivos.some((file) =>
      proc.empresas.some(
        (emp) => emp.status === 'PRONTO' && this.matchArquivo(file, emp.codigo, proc, inicio)
      )
    );
  }

  private matchArquivo(file: any, codigo: string, proc: RpaProcessamento, inicio: Date): boolean {
    const base = [String(file?.key || ''), String(file?.fileName || ''), String(file?.rpaKey || '')]
      .join(' ')
      .toUpperCase();

    if (!this.isArquivoPendencia(base, proc.tipoDivida)) return false;

    const codigoUp = String(codigo || '').toUpperCase();
    if (codigoUp === 'GERAL') {
      return this.arquivoNaJanelaDoProcessamento(file, inicio);
    }
    if (!codigoUp || !base.includes(codigoUp)) return false;

    return this.arquivoNaJanelaDoProcessamento(file, inicio);
  }

  private arquivoNaJanelaDoProcessamento(file: any, inicio: Date): boolean {
    const dataArquivo = this.getDataArquivo(file);
    if (!dataArquivo) return true;
    return this.isMesmaExecucao(dataArquivo, inicio);
  }

  private isArquivoPendencia(base: string, tipoDivida: 'inscrita' | 'nao-inscrita'): boolean {
    if (tipoDivida === 'nao-inscrita') {
      return (
        base.includes('CONSULTA-PENDENCIAS-NAO-INSCRITA') ||
        base.includes('PENDENCIA-ESTADUAL-NAO-INSCRITA') ||
        (base.includes('PENDENCIA') && base.includes('NAO') && base.includes('INSCRIT'))
      );
    }

    const temNaoInscrita =
      base.includes('NAO-INSCRITA') ||
      base.includes('NAO_INSCRITA') ||
      base.includes('NÃO INSCRITA') ||
      base.includes('NAO INSCRITA') ||
      base.includes('CONSULTA-PENDENCIAS-NAO-INSCRITA');
    if (temNaoInscrita) return false;

    return (
      base.includes('CONSULTA-PENDENCIAS-INSCRITA') ||
      base.includes('PENDENCIA-ESTADUAL-INSCRITA') ||
      (base.includes('PENDENCIA') && base.includes('INSCRIT'))
    );
  }

  private getDataArquivo(file: any): Date | null {
    const raw =
      file?.dataHoraProc ??
      file?.dataHoraProcessamento ??
      file?.createdAt ??
      file?.updatedAt ??
      file?.date;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private isMesmaExecucao(dataArquivo: Date, inicioExecucao: Date): boolean {
    const diffMs = dataArquivo.getTime() - inicioExecucao.getTime();
    const toleranciaAntesMs = 5 * 60 * 1000;
    const janelaDepoisMs = 48 * 60 * 60 * 1000;
    return diffMs >= -toleranciaAntesMs && diffMs <= janelaDepoisMs;
  }

  private extrairCodigos(response: any, codigoDigitado: string): string[] {
    const enfileirados = this.normalizeArray(
      response?.cnpjs
        ?? response?.cnpjsEnfileirados
        ?? response?.codigosEnfileirados
        ?? response?.codigoEmpresa
    );
    const fallback = codigoDigitado ? [codigoDigitado.replace(/\D/g, '')] : [];
    const base = enfileirados.length > 0 ? enfileirados : fallback;
    return base.length > 0 ? base : ['GERAL'];
  }

  private normalizeArray(value: any): string[] {
    if (Array.isArray(value)) {
      return value.map((v) => String(v || '').trim()).filter(Boolean);
    }
    if (value == null) return [];
    const single = String(value).trim();
    return single ? [single] : [];
  }

  private novoId(): string {
    return `rpa-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  private upsert(novo: RpaProcessamento): void {
    const lista = [novo, ...this.processamentosSubject.value.filter((p) => p.id !== novo.id)].slice(0, 10);
    this.persist(lista);
  }

  private persist(lista: RpaProcessamento[]): void {
    this.processamentosSubject.next(lista);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(lista));
  }

  private loadFromStorage(): RpaProcessamento[] {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as RpaProcessamento[];
      return Array.isArray(parsed) ? parsed.map((p) => ({ ...p, empresas: p.empresas ?? [] })) : [];
    } catch {
      return [];
    }
  }

}
